import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Inbox, Loader2, Plus, MessageSquare, Phone, Mail, Globe, UserPlus,
  Ban, Send, Settings2, Copy, CheckCircle2, Zap,
} from "lucide-react";
import { sr } from "../../lib/sr";

const CHANNEL = {
  web: { icon: Globe, label: "Web form", color: "var(--blue)" },
  sms: { icon: MessageSquare, label: "SMS", color: "#7c3aed" },
  phone: { icon: Phone, label: "Call", color: "#059669" },
  email: { icon: Mail, label: "Email", color: "#d97706" },
  manual: { icon: UserPlus, label: "Manual", color: "var(--muted)" },
};
const STATUSES = ["new", "contacted", "won", "lost"];
const STATUS_BADGE = { new: "sr-badge", contacted: "sr-badge sr-badge-amber", won: "sr-badge sr-badge-green", lost: "sr-badge sr-badge-gray" };

export default function Leads() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [acting, setActing] = useState(null);
  const [ar, setAr] = useState(null);
  const [savingAr, setSavingAr] = useState(false);

  const load = (f = filter) => sr.leads(f).then((d) => {
    setData(d);
    if (d?.config) setAr({ enabled: d.config.auto_reply_enabled, text: d.config.auto_reply_text });
  }).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, [filter]);

  const saveAutoReply = async () => {
    setSavingAr(true);
    try { await sr.updateLeadSettings({ auto_reply_enabled: ar.enabled, auto_reply_text: ar.text }); toast.success("Auto-reply saved"); }
    catch { toast.error("Could not save"); } finally { setSavingAr(false); }
  };

  const addLead = async () => {
    if (!form.name && !form.phone && !form.email) return toast.error("Add a name, phone or email");
    setBusy(true);
    try {
      await sr.createLead(form);
      setForm({ name: "", phone: "", email: "", message: "" });
      await load();
      toast.success("Lead added to your inbox");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add lead"); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await sr.updateLeadStatus(id, status); await load(); } catch { toast.error("Could not update"); }
  };
  const optOut = async (id) => {
    setActing(id + "o");
    try { await sr.optOutLead(id); await load(); toast.success("Contact opted out & suppressed"); }
    catch { toast.error("Could not opt out"); } finally { setActing(null); }
  };
  const sendReply = async (id) => {
    if (replyText.trim().length < 2) return;
    setActing(id + "r");
    try { await sr.replyLead(id, replyText.trim()); setReplyFor(null); setReplyText(""); await load(); toast.success("SMS sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setActing(null); }
  };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;

  const cfg = data.config || {};
  const s = data.summary || {};

  return (
    <div data-testid="leads-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><Inbox size={12} /> Lead Concierge</span>
          <h1 style={{ fontSize: 30 }}>One inbox for every lead</h1>
          <p className="sr-muted" style={{ marginTop: 6 }}>Web, SMS, phone and email leads land here — with one-tap opt-out and a simple pipeline.</p>
        </div>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setShowConfig((v) => !v)} data-testid="leads-config-toggle">
          <Settings2 size={15} /> Channels
        </button>
      </div>

      {/* Channel config */}
      {showConfig && (
        <div className="sr-card" style={{ marginBottom: 16 }} data-testid="leads-config">
          <div className="sr-eyebrow" style={{ marginBottom: 10 }}>Connect your channels</div>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
            <div>
              <p style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><MessageSquare size={14} /> SMS & phone</p>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>
                {cfg.sms_number ? <>Tracked line <strong>{cfg.sms_number}</strong>. </> : <>Add a Twilio number in settings to activate. </>}
                {cfg.sms_inbound_ready ? <span className="sr-badge sr-badge-green" style={{ marginLeft: 4 }}>Twilio connected</span> : <span className="sr-badge sr-badge-gray">Connect Twilio</span>}
              </p>
              <p className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>Point your Twilio SMS & Voice webhooks to:</p>
              <code style={{ fontSize: 11, wordBreak: "break-all" }} data-testid="leads-sms-webhook">{cfg.sms_webhook}</code>
            </div>
            <div>
              <p style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><Mail size={14} /> Email</p>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>Forward or route mail to this address:</p>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <code style={{ fontSize: 12 }} data-testid="leads-email-inbound">{cfg.email_inbound || "—"}</code>
                {cfg.email_inbound && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => copy(cfg.email_inbound)}><Copy size={12} /></button>}
              </div>
            </div>
            <div>
              <p style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}><Globe size={14} /> Website form</p>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>Paste this on your site to capture leads:</p>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => copy(cfg.web_embed)} data-testid="leads-copy-embed"><Copy size={12} /> Copy embed code</button>
            </div>
          </div>
          {ar && (
            <div style={{ marginTop: 16, borderTop: "1px solid var(--border)", paddingTop: 16 }} data-testid="autoreply-config">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <p style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                  <Zap size={14} /> Instant auto-reply
                </p>
                <button onClick={() => setAr({ ...ar, enabled: !ar.enabled })} data-testid="autoreply-toggle"
                  className={`sr-btn sr-btn-sm ${ar.enabled ? "sr-btn-primary" : "sr-btn-ghost"}`}>
                  {ar.enabled ? "On" : "Off"}
                </button>
              </div>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Instantly texts new SMS &amp; web-form leads. Use <code>{"{business}"}</code> for your business name.
              </p>
              <textarea className="sr-input" rows={3} value={ar.text} onChange={(e) => setAr({ ...ar, text: e.target.value })}
                disabled={!ar.enabled} style={{ marginTop: 8, resize: "vertical", opacity: ar.enabled ? 1 : 0.6 }} data-testid="autoreply-text" />
              <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 8 }} onClick={saveAutoReply} disabled={savingAr} data-testid="autoreply-save">
                {savingAr ? <Loader2 size={14} className="sr-spin" /> : <><CheckCircle2 size={14} /> Save auto-reply</>}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Summary + filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }} data-testid="leads-filters">
        <button onClick={() => setFilter(null)} className={`sr-btn sr-btn-sm ${!filter ? "sr-btn-primary" : "sr-btn-ghost"}`} data-testid="leads-filter-all">All · {s.total || 0}</button>
        {STATUSES.map((st) => (
          <button key={st} onClick={() => setFilter(st)} className={`sr-btn sr-btn-sm ${filter === st ? "sr-btn-primary" : "sr-btn-ghost"}`} data-testid={`leads-filter-${st}`}>
            {st[0].toUpperCase() + st.slice(1)} · {s[st] || 0}
          </button>
        ))}
        {s.opted_out > 0 && <span className="sr-badge sr-badge-gray" style={{ alignSelf: "center" }}><Ban size={11} /> {s.opted_out} opted out</span>}
      </div>

      {/* Manual add */}
      <div className="sr-card" style={{ marginBottom: 16 }} data-testid="leads-add-card">
        <div className="sr-eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Plus size={13} /> Log a lead</div>
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
          <input className="sr-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" />
          <input className="sr-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" />
          <input className="sr-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" />
        </div>
        <textarea className="sr-input" rows={2} placeholder="What do they need?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} style={{ marginTop: 10, resize: "vertical" }} data-testid="lead-message" />
        <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} onClick={addLead} disabled={busy} data-testid="lead-add-btn">
          {busy ? <Loader2 size={15} className="sr-spin" /> : <><Plus size={15} /> Add to inbox</>}
        </button>
      </div>

      {/* Inbox list */}
      {(data.leads || []).length === 0 ? (
        <div className="sr-card" data-testid="leads-empty">
          <p className="sr-muted">No leads yet. Share your web form, connect your Twilio number, or log one above — they all land here.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {data.leads.map((l) => {
            const ch = CHANNEL[l.channel] || CHANNEL.manual;
            const CIcon = ch.icon;
            return (
              <div key={l.id} className="sr-card" data-testid={`lead-${l.id}`} style={l.opted_out ? { opacity: 0.7 } : {}}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 200, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: ch.color, fontSize: 12, fontWeight: 700 }}>
                        <CIcon size={14} /> {ch.label}
                      </span>
                      {l.opted_out && <span className="sr-badge sr-badge-gray"><Ban size={10} /> Opted out</span>}
                    </div>
                    <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 16, marginTop: 6 }}>{l.name || l.phone || l.email || "New lead"}</div>
                    <p className="sr-muted" style={{ fontSize: 13, marginTop: 2 }}>
                      {[l.phone, l.email].filter(Boolean).join(" · ") || "No contact info"}
                    </p>
                    {l.message && <p style={{ fontSize: 13, marginTop: 8, color: "var(--ink,#1f2a44)" }}>{l.message}</p>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                    <select className="sr-select" value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} style={{ minWidth: 130 }}>
                      {STATUSES.map((st) => <option key={st} value={st}>{st[0].toUpperCase() + st.slice(1)}</option>)}
                    </select>
                    <div style={{ display: "flex", gap: 6 }}>
                      {l.phone && !l.opted_out && cfg.sms_outbound_ready && (
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setReplyFor(replyFor === l.id ? null : l.id); setReplyText(""); }} data-testid={`lead-reply-${l.id}`}><Send size={13} /> Reply</button>
                      )}
                      {!l.opted_out && (
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => optOut(l.id)} disabled={acting === l.id + "o"} data-testid={`lead-optout-${l.id}`}>
                          {acting === l.id + "o" ? <Loader2 size={13} className="sr-spin" /> : <><Ban size={13} /> Opt out</>}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {replyFor === l.id && (
                  <div style={{ marginTop: 12, display: "flex", gap: 8 }} data-testid={`lead-reply-box-${l.id}`}>
                    <input className="sr-input" placeholder="Type an SMS reply…" value={replyText} onChange={(e) => setReplyText(e.target.value)} style={{ flex: 1 }} />
                    <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => sendReply(l.id)} disabled={acting === l.id + "r"}>
                      {acting === l.id + "r" ? <Loader2 size={14} className="sr-spin" /> : <><Send size={14} /> Send</>}
                    </button>
                  </div>
                )}
                {(l.timeline || []).length > 1 && (
                  <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 8 }}>
                    {l.timeline.slice(-3).reverse().map((t, i) => (
                      <p key={i} className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}>
                        <CheckCircle2 size={11} style={{ color: "var(--success)" }} /> {t.text}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
